/**
 * Admin → Alerts (Deivydas 2026-10-13): who is emailed when Zoye drops the
 * ball, for each alert type, with its own switch and threshold.
 *
 * The check runs on the worker every 10 minutes. "Check now" runs the same
 * check on demand — as a dry run (reports what it would send) or for real —
 * which is how the alerts are tested on a test ticket without waiting.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, Check, Mail } from 'lucide-react';
import {
  useAlerts,
  useRunAlertSweep,
  useTestAlert,
  useUpdateAlert,
  type AlertTypeRow,
  type SweepResult,
} from '../api/alerts';
import { ApiError } from '../api/client';
import { Badge, Button, Card, CardHeader, Table, Td, Th } from '../components/ui/primitives';
import { formatDateTime } from '../lib/format';

function Problem({ error }: { error: unknown }) {
  if (!error) return null;
  const message = error instanceof ApiError ? error.message : 'Something went wrong.';
  return (
    <div className="flex items-start gap-2 bg-stop-100 px-3 py-2 text-xs text-stop-700">
      <AlertTriangle size={14} strokeWidth={1.75} className="mt-px shrink-0" aria-hidden />
      <p>{message}</p>
    </div>
  );
}

/** Commas, semicolons, spaces or new lines between addresses. */
const parseRecipients = (text: string): string[] =>
  text
    .split(/[\s,;]+/)
    .map((value) => value.trim())
    .filter(Boolean);

export function AlertsTab() {
  const { data, isPending, error } = useAlerts();
  const sweep = useRunAlertSweep();

  if (isPending) return <Card><p className="px-4 py-6 text-sm text-ink-400">Loading…</p></Card>;
  if (error) return <Card><Problem error={error} /></Card>;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader title="Alerts" meta="Emails when Zoye drops the ball — checked every 10 minutes" />
        <div className="px-4 py-3">
          {data.email.configured ? (
            <p className="flex items-center gap-1.5 text-xs text-ok-700">
              <Mail size={13} strokeWidth={2} aria-hidden /> Sending from {data.email.from} via {data.email.host}
            </p>
          ) : (
            <p className="flex items-center gap-1.5 text-xs text-stop-700">
              <AlertTriangle size={13} strokeWidth={2} aria-hidden /> Email is not configured: set SMTP_USER and
              SMTP_PASS on the server. Alerts are detected but not sent, and go out once it is set.
            </p>
          )}
          <p className="mt-1 text-2xs text-ink-400">
            Each alert is sent once per ticket event. An alert with no recipients sends nothing. Every email names
            the Zendesk ticket and links to it.
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Button size="sm" disabled={sweep.isPending} onClick={() => sweep.mutate({ dryRun: true })}>
              Check now (dry run)
            </Button>
            <Button
              size="sm"
              variant="danger"
              disabled={sweep.isPending}
              onClick={() => {
                if (window.confirm('Run the check now and email any alerts that are due?')) sweep.mutate({ dryRun: false });
              }}
            >
              Check now and send
            </Button>
            {sweep.isPending ? <span className="text-xs text-ink-400">Checking…</span> : null}
          </div>
          <Problem error={sweep.error} />
          {sweep.data ? <SweepReport result={sweep.data} rows={data.types} /> : null}
        </div>
      </Card>

      {data.types.map((row) => (
        <AlertRuleCard key={row.type} row={row} canTest={data.email.configured} />
      ))}

      <Card>
        <CardHeader title="Sent" meta="the last 50 alerts emailed" />
        {data.recent.length === 0 ? (
          <p className="px-4 py-6 text-center text-sm text-ink-400">No alerts sent yet.</p>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>When</Th>
                <Th>Alert</Th>
                <Th>Ticket</Th>
                <Th>What happened</Th>
              </tr>
            </thead>
            <tbody>
              {data.recent.map((alert, index) => (
                <tr key={`${alert.at}-${index}`} className="align-top">
                  <Td className="whitespace-nowrap text-xs text-ink-500">{formatDateTime(alert.at)}</Td>
                  <Td className="text-xs">
                    {data.types.find((row) => row.type === alert.type)?.label ?? alert.type ?? '—'}
                  </Td>
                  <Td className="whitespace-nowrap font-mono text-xs">
                    {alert.ticketId ? (
                      <Link to={`/tickets/${alert.ticketId}`} className="hover:text-accent-700">
                        #{alert.externalId}
                      </Link>
                    ) : (
                      `#${alert.externalId ?? '—'}`
                    )}
                    {alert.brand ? <div className="font-sans text-2xs text-ink-400">{alert.brand}</div> : null}
                  </Td>
                  <Td className="text-xs text-ink-700">{alert.detail ?? '—'}</Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
    </div>
  );
}

function SweepReport({ result, rows }: { result: SweepResult; rows: AlertTypeRow[] }) {
  const entries = Object.entries(result.byType);
  return (
    <div className="mt-3 rounded bg-surface-sunken px-3 py-2 text-xs text-ink-700">
      <p>
        {result.candidates} due · {result.alreadySent} already sent · {result.droppedByZendesk} dropped after
        checking Zendesk
      </p>
      {entries.length === 0 ? (
        <p className="mt-1 text-ink-400">Nothing new to alert.</p>
      ) : (
        <ul className="mt-1 space-y-0.5">
          {entries.map(([type, entry]) => (
            <li key={type}>
              <span className="font-medium">{rows.find((row) => row.type === type)?.label ?? type}</span>:{' '}
              {entry.tickets.map((id) => `#${id}`).join(', ')} —{' '}
              {entry.sent ? <span className="text-ok-700">sent</span> : <span className="text-ink-500">{entry.skipped}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function AlertRuleCard({ row, canTest }: { row: AlertTypeRow; canTest: boolean }) {
  const update = useUpdateAlert();
  const test = useTestAlert();
  const [threshold, setThreshold] = useState<string | null>(null);
  const [recipients, setRecipients] = useState<string | null>(null);

  const thresholdText = threshold ?? String(row.threshold);
  const parsedThreshold = Number(thresholdText);
  const thresholdValid =
    Number.isInteger(parsedThreshold) && parsedThreshold >= row.min && parsedThreshold <= row.max;
  const recipientsText = recipients ?? row.recipients.join(', ');
  const parsedRecipients = parseRecipients(recipientsText);
  const badRecipient = parsedRecipients.find((value) => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value));
  const dirty =
    (thresholdValid && parsedThreshold !== row.threshold) ||
    parsedRecipients.join(',') !== row.recipients.join(',');

  const save = () =>
    update.mutate(
      { type: row.type, threshold: parsedThreshold, recipients: parsedRecipients },
      {
        onSuccess: () => {
          setThreshold(null);
          setRecipients(null);
        },
      },
    );

  return (
    <Card>
      <div className="flex flex-wrap items-start gap-3 px-4 py-3">
        <div className="min-w-[240px] flex-1">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-ink-900">{row.label}</span>
            <Badge tone={row.enabled ? 'fact' : 'neutral'}>{row.enabled ? 'on' : 'off'}</Badge>
          </div>
          <p className="mt-0.5 text-xs text-ink-500">{row.description}</p>
        </div>
        <Button
          size="sm"
          variant={row.enabled ? 'ghost' : 'secondary'}
          disabled={update.isPending}
          onClick={() => update.mutate({ type: row.type, enabled: !row.enabled })}
        >
          {row.enabled ? 'Disable' : 'Enable'}
        </Button>
      </div>

      <div className="grid gap-3 border-t border-line px-4 py-3 sm:grid-cols-[160px_1fr]">
        <label className="block">
          <span className="eyebrow">
            After ({row.unit})
          </span>
          <input
            type="text"
            inputMode="numeric"
            value={thresholdText}
            onChange={(event) => setThreshold(event.target.value)}
            className="mt-1 w-24 rounded-sm bg-surface px-2 py-1 text-sm text-ink-900 shadow-hairline"
          />
          {!thresholdValid ? (
            <span className="mt-0.5 block text-2xs text-stop-700">
              {row.min}–{row.max} {row.unit}
            </span>
          ) : null}
        </label>
        <label className="block">
          <span className="eyebrow">Email to</span>
          <textarea
            rows={2}
            value={recipientsText}
            onChange={(event) => setRecipients(event.target.value)}
            placeholder="name@theimpactbrands.com, other@theimpactbrands.com"
            className="mt-1 w-full rounded-sm bg-surface px-2 py-1 text-sm text-ink-900 shadow-hairline placeholder:text-ink-300"
          />
          {badRecipient ? (
            <span className="mt-0.5 block text-2xs text-stop-700">&ldquo;{badRecipient}&rdquo; is not an email address.</span>
          ) : (
            <span className="mt-0.5 block text-2xs text-ink-400">Separate addresses with commas or new lines.</span>
          )}
        </label>
      </div>

      <div className="flex flex-wrap items-center gap-2 border-t border-line px-4 py-2.5">
        <Button
          size="sm"
          variant="primary"
          disabled={!dirty || !thresholdValid || Boolean(badRecipient) || update.isPending}
          onClick={save}
        >
          {update.isPending ? 'Saving…' : 'Save'}
        </Button>
        <Button
          size="sm"
          disabled={!canTest || row.recipients.length === 0 || test.isPending}
          title={!canTest ? 'Email is not configured on the server' : row.recipients.length === 0 ? 'Add and save a recipient first' : undefined}
          onClick={() => test.mutate({ type: row.type })}
        >
          {test.isPending ? 'Sending…' : 'Send test email'}
        </Button>
        {update.isSuccess && !dirty ? (
          <span className="flex items-center gap-1 text-xs text-ok-700">
            <Check size={13} strokeWidth={2.5} aria-hidden /> saved
          </span>
        ) : null}
        {test.data ? <span className="text-xs text-ok-700">Test sent to {test.data.sentTo.join(', ')}</span> : null}
      </div>
      <Problem error={update.error ?? test.error} />
    </Card>
  );
}
